import type { OptimizationStudy, UserPlan, Workbench } from '@minglun/domain';

const clone = <T,>(value: T): T => structuredClone(value);
import { shareUnchanged } from './ui/immutable-state';

function studyFromPlan(plan: UserPlan, id: string, name: string, createdAt: string, description?: string): OptimizationStudy {
  return {
    id,
    name,
    ...(description ? { description } : {}),
    createdAt,
    updatedAt: plan.updatedAt || createdAt,
    baseAttributes: plan.baseAttributes,
    objectives: plan.objectives,
    inventory: plan.inventory,
    solver: plan.solver,
    ...(plan.lastResult ? { lastResult: plan.lastResult } : {}),
  };
}

function applyStudy(plan: UserPlan, study: OptimizationStudy): UserPlan {
  const { lastResult: _previousResult, ...withoutLastResult } = plan;
  return {
    ...withoutLastResult,
    name: study.name,
    activeStudyId: study.id,
    baseAttributes: study.baseAttributes,
    objectives: study.objectives,
    inventory: study.inventory,
    solver: study.solver,
    ...(study.lastResult ? { lastResult: study.lastResult } : {}),
  };
}

export function initializeStudies(plan: UserPlan): UserPlan {
  const now = plan.updatedAt || new Date().toISOString();
  const studies = (plan.studies ?? []).filter((study) => study.id && study.name);
  if (!studies.length) {
    const migrated = studyFromPlan(plan, 'study.default', plan.name || '默认研究', now, '由原有方案自动迁移');
    const solverHistory = plan.solverHistory.map((entry) => entry.studyId ? entry : { ...entry, studyId: migrated.id, studyName: migrated.name });
    return applyStudy({ ...plan, studies: [migrated], activeStudyId: migrated.id, solverHistory }, migrated);
  }
  const active = studies.find((study) => study.id === plan.activeStudyId) ?? studies[0]!;
  const solverHistory = plan.solverHistory.map((entry) => entry.studyId ? entry : { ...entry, studyId: active.id, studyName: active.name });
  return applyStudy({ ...plan, studies, activeStudyId: active.id, solverHistory }, active);
}

export function syncActiveStudy(plan: UserPlan): UserPlan {
  const initialized = plan.studies?.length ? plan : initializeStudies(plan);
  const activeId = initialized.activeStudyId ?? initialized.studies?.[0]?.id;
  if (!activeId) return initialized;
  const studies = initialized.studies ?? [];
  const current = studies.find((study) => study.id === activeId);
  if (!current) return initializeStudies({ ...initialized, activeStudyId: studies[0]!.id });
  const next = shareUnchanged(current, { ...studyFromPlan(initialized, current.id, initialized.name, current.createdAt, current.description), updatedAt: current.updatedAt });
  if (next === current)
    return initialized;
  return { ...initialized, name: next.name, studies: studies.map((study) => study.id === activeId ? { ...next, updatedAt: new Date().toISOString() } : study) };
}

export function selectStudy(plan: UserPlan, studyId: string): UserPlan {
  const synced = syncActiveStudy(plan);
  const study = synced.studies?.find((item) => item.id === studyId);
  return study ? applyStudy(synced, study) : synced;
}

export function createBlankStudy(workbench: Workbench, plan: UserPlan, name: string, description = ''): OptimizationStudy {
  const algorithm = workbench.defaults.solverAlgorithms.find((item) => item.isEnabled) ?? workbench.defaults.solverAlgorithms[0];
  if (!algorithm) throw new Error('数据库没有可用的求解器配置。');
  const timestamp = new Date().toISOString();
  return {
    id: `study.${crypto.randomUUID()}`,
    name,
    ...(description.trim() ? { description: description.trim() } : {}),
    createdAt: timestamp,
    updatedAt: timestamp,
    baseAttributes: clone(workbench.defaults.userBaseAttributeTemplate.values),
    objectives: workbench.optimizationMetrics.map((metric) => ({ metricId: metric.id, weight: metric.defaultWeight, isEnabled: metric.defaultWeight > 0 })),
    inventory: workbench.characters.map((character) => ({ characterId: character.id, wheelCount: 0, backpackFragmentCount: 0, itemFragmentCount: 0, ignoreFragments: false })),
    solver: {
      algorithmId: algorithm.id,
      includeDestinyWheel: true,
      ignoredFragmentGroups: [],
      ssrWheelChoices: 0,
      ssrChoiceBalanceEnabled: false,
      preferFewerWheels: true,
      fragmentAnalysisEnabled: false,
      parameters: algorithm.parameters.filter((parameter) => parameter.key !== 'time_limit_seconds' && parameter.key !== 'threads').map((parameter) => ({ key: parameter.key, value: parameter.defaultValue })),
    },
  };
}

export function addStudy(plan: UserPlan, study: OptimizationStudy): UserPlan {
  const synced = syncActiveStudy(plan);
  return applyStudy({ ...synced, studies: [...(synced.studies ?? []), clone(study)] }, study);
}

export function duplicateStudy(plan: UserPlan, sourceId: string, name: string, description = ''): UserPlan {
  const synced = syncActiveStudy(plan);
  const source = synced.studies?.find((study) => study.id === sourceId);
  if (!source) return synced;
  const timestamp = new Date().toISOString();
  const { lastResult: _sourceResult, description: _sourceDescription, ...sourceConfiguration } = clone(source);
  const copy: OptimizationStudy = {
    ...sourceConfiguration,
    id: `study.${crypto.randomUUID()}`,
    name,
    ...(description.trim() ? { description: description.trim() } : {}),
    createdAt: timestamp,
    updatedAt: timestamp,
  };
  return applyStudy({ ...synced, studies: [...(synced.studies ?? []), copy] }, copy);
}

export function renameStudy(plan: UserPlan, studyId: string, name: string, description?: string): UserPlan {
  const synced = syncActiveStudy(plan);
  const studies = (synced.studies ?? []).map((study) => {
    if (study.id !== studyId) return study;
    const { description: _previousDescription, ...withoutDescription } = study;
    return { ...withoutDescription, name, ...(description?.trim() ? { description: description.trim() } : {}), updatedAt: new Date().toISOString() };
  });
  return { ...synced, ...(synced.activeStudyId === studyId ? { name } : {}), studies };
}

export function deleteStudy(plan: UserPlan, studyId: string): UserPlan {
  const synced = syncActiveStudy(plan);
  const studies = (synced.studies ?? []).filter((study) => study.id !== studyId);
  if (!studies.length) return synced;
  const withoutLinkedResults = {
    ...synced,
    studies,
    solverHistory: synced.solverHistory.filter((entry) => entry.studyId !== studyId),
  };
  if (synced.activeStudyId !== studyId) return withoutLinkedResults;
  return applyStudy(withoutLinkedResults, studies[0]!);
}

export function activeStudy(plan: UserPlan): OptimizationStudy | undefined {
  return plan.studies?.find((study) => study.id === plan.activeStudyId) ?? plan.studies?.[0];
}
